/**
 * Deal Comments Panel — Sprint 18 F006
 *
 * Threaded comments panel for deal detail pages, enabling team collaboration.
 * Supports adding, editing, deleting comments with thread replies and
 * internal/external visibility toggle.
 *
 * Placement: Activity tab of deal-detail.tsx (via fragment)
 *
 * API endpoints used (F003):
 *   GET    /api/crm/deals/:dealId/comments
 *   POST   /api/crm/deals/:dealId/comments
 *   PATCH  /api/crm/deals/:dealId/comments/:id
 *   DELETE /api/crm/deals/:dealId/comments/:id
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  MessageSquare,
  Reply,
  Edit,
  Trash2,
  Loader2,
  Send,
  X,
  ChevronDown,
  ChevronRight,
  Lock,
  Globe,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ============================================================================
// TYPES
// ============================================================================

interface DealComment {
  id: string;
  dealId: string;
  commentText: string;
  isInternal: boolean;
  parentCommentId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  isEdited: boolean;
  organisationId: string;
  replyCount: number;
}

interface DealCommentsPanelProps {
  dealId: string;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Format relative time string (e.g. "2 hours ago", "just now")
 */
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? "s" : ""} ago`;
  if (diffHr < 24) return `${diffHr} hour${diffHr !== 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;

  return date.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * Generate a stable colour based on a string (user ID or name).
 * Returns a Tailwind-compatible bg class.
 */
const AVATAR_COLOURS = [
  "bg-blue-500",
  "bg-emerald-500",
  "bg-purple-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-cyan-500",
  "bg-indigo-500",
  "bg-teal-500",
];

function getAvatarColour(seed: string | null): string {
  if (!seed) return AVATAR_COLOURS[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLOURS[Math.abs(hash) % AVATAR_COLOURS.length];
}

/**
 * Get initials from a user ID (placeholder — real app would look up user name).
 */
function getInitials(createdBy: string | null): string {
  if (!createdBy) return "??";
  // Use first 2 chars of UUID as a placeholder for initials
  return createdBy.substring(0, 2).toUpperCase();
}

// ============================================================================
// COMMENT COMPONENT
// ============================================================================

interface CommentItemProps {
  comment: DealComment;
  allComments: DealComment[];
  dealId: string;
  onReply: (commentId: string) => void;
  onEdit: (commentId: string) => void;
  onDelete: (comment: DealComment) => void;
  replyingTo: string | null;
  editingId: string | null;
  editText: string;
  setEditText: (t: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  replyText: string;
  setReplyText: (t: string) => void;
  onSubmitReply: () => void;
  onCancelReply: () => void;
  isReplyPending: boolean;
  isEditPending: boolean;
  depth?: number;
}

function CommentItem({
  comment,
  allComments,
  dealId,
  onReply,
  onEdit,
  onDelete,
  replyingTo,
  editingId,
  editText,
  setEditText,
  onSaveEdit,
  onCancelEdit,
  replyText,
  setReplyText,
  onSubmitReply,
  onCancelReply,
  isReplyPending,
  isEditPending,
  depth = 0,
}: CommentItemProps) {
  const [isThreadExpanded, setIsThreadExpanded] = useState(false);

  const replies = allComments.filter((c) => c.parentCommentId === comment.id);
  const hasReplies = replies.length > 0;
  const isEditing = editingId === comment.id;
  const isReplying = replyingTo === comment.id;

  return (
    <div className={depth > 0 ? "ml-6 pl-3 border-l-2 border-muted" : ""}>
      <div className="flex gap-3 py-2" data-testid={`comment-${comment.id}`}>
        {/* Avatar */}
        <div
          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium ${getAvatarColour(comment.createdBy)}`}
        >
          {getInitials(comment.createdBy)}
        </div>

        <div className="flex-1 min-w-0">
          {/* Header: author + timestamp + badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">
              {comment.createdBy ? comment.createdBy.substring(0, 8) + "..." : "Unknown User"}
            </span>
            <span className="text-xs text-muted-foreground">
              {formatRelativeTime(comment.createdAt)}
            </span>
            {comment.isEdited && (
              <span className="text-xs text-muted-foreground italic">Edited</span>
            )}
            {comment.isInternal ? (
              <Badge variant="secondary" className="text-xs px-1.5 py-0">
                <Lock className="h-3 w-3 mr-0.5" />
                Internal
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs px-1.5 py-0">
                <Globe className="h-3 w-3 mr-0.5" />
                Shared
              </Badge>
            )}
          </div>

          {/* Comment body or edit textarea */}
          {isEditing ? (
            <div className="mt-1 space-y-2">
              <Textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="min-h-[60px] text-sm"
                data-testid="edit-comment-input"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={onSaveEdit}
                  disabled={!editText.trim() || isEditPending}
                  data-testid="button-save-edit"
                >
                  {isEditPending ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : null}
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onCancelEdit}
                  data-testid="button-cancel-edit"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm mt-0.5 whitespace-pre-wrap">{comment.commentText}</p>
          )}

          {/* Action buttons */}
          {!isEditing && (
            <div className="flex items-center gap-1 mt-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-muted-foreground"
                onClick={() => onReply(comment.id)}
                data-testid={`button-reply-${comment.id}`}
              >
                <Reply className="h-3 w-3 mr-1" />
                Reply
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-muted-foreground"
                onClick={() => onEdit(comment.id)}
                data-testid={`button-edit-${comment.id}`}
              >
                <Edit className="h-3 w-3 mr-1" />
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-muted-foreground text-destructive"
                onClick={() => onDelete(comment)}
                data-testid={`button-delete-${comment.id}`}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Delete
              </Button>
            </div>
          )}

          {/* Thread expand/collapse */}
          {hasReplies && !isThreadExpanded && (
            <button
              className="flex items-center gap-1 text-xs text-primary mt-1 hover:underline"
              onClick={() => setIsThreadExpanded(true)}
              data-testid={`button-expand-thread-${comment.id}`}
            >
              <ChevronRight className="h-3 w-3" />
              {replies.length} {replies.length === 1 ? "reply" : "replies"}
            </button>
          )}

          {hasReplies && isThreadExpanded && (
            <button
              className="flex items-center gap-1 text-xs text-primary mt-1 hover:underline"
              onClick={() => setIsThreadExpanded(false)}
              data-testid={`button-collapse-thread-${comment.id}`}
            >
              <ChevronDown className="h-3 w-3" />
              Hide replies
            </button>
          )}

          {/* Reply input */}
          {isReplying && (
            <div className="mt-2 space-y-2">
              <p className="text-xs text-muted-foreground">
                Replying to {comment.createdBy ? comment.createdBy.substring(0, 8) + "..." : "comment"}
              </p>
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write a reply..."
                className="min-h-[60px] text-sm"
                data-testid="reply-input"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={onSubmitReply}
                  disabled={!replyText.trim() || isReplyPending}
                  data-testid="button-submit-reply"
                >
                  {isReplyPending ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <Send className="h-3 w-3 mr-1" />
                  )}
                  Reply
                </Button>
                <Button size="sm" variant="ghost" onClick={onCancelReply}>
                  <X className="h-3 w-3 mr-1" />
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Nested replies (expanded) */}
      {isThreadExpanded &&
        replies.map((reply) => (
          <CommentItem
            key={reply.id}
            comment={reply}
            allComments={allComments}
            dealId={dealId}
            onReply={onReply}
            onEdit={onEdit}
            onDelete={onDelete}
            replyingTo={replyingTo}
            editingId={editingId}
            editText={editText}
            setEditText={setEditText}
            onSaveEdit={onSaveEdit}
            onCancelEdit={onCancelEdit}
            replyText={replyText}
            setReplyText={setReplyText}
            onSubmitReply={onSubmitReply}
            onCancelReply={onCancelReply}
            isReplyPending={isReplyPending}
            isEditPending={isEditPending}
            depth={depth + 1}
          />
        ))}
    </div>
  );
}

// ============================================================================
// MAIN PANEL COMPONENT
// ============================================================================

export function DealCommentsPanel({ dealId }: DealCommentsPanelProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State
  const [newCommentText, setNewCommentText] = useState("");
  const [isInternal, setIsInternal] = useState(true);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [deleteComment, setDeleteComment] = useState<DealComment | null>(null);

  // Query key for invalidation
  const commentsQueryKey = [`/api/crm/deals/${dealId}/comments`];

  // Fetch all comments (flat list, including replies)
  const { data: comments = [], isLoading } = useQuery<DealComment[]>({
    queryKey: commentsQueryKey,
    enabled: !!dealId,
  });

  // Top-level comments (no parent)
  const topLevelComments = comments.filter((c) => !c.parentCommentId);
  const commentCount = comments.length;

  // ==============================
  // MUTATIONS
  // ==============================

  // Create comment — append server response to cache (no refetch to avoid branch DB staleness)
  const createMutation = useMutation({
    mutationFn: async (data: { commentText: string; isInternal: boolean; parentCommentId?: string }) => {
      const res = await apiRequest("POST", `/api/crm/deals/${dealId}/comments`, data);
      return res.json() as Promise<DealComment>;
    },
    onSuccess: (newComment: DealComment) => {
      const currentComments = queryClient.getQueryData<DealComment[]>(commentsQueryKey) ?? [];
      queryClient.setQueryData(commentsQueryKey, [...currentComments, newComment]);
      toast({ title: "Comment added", description: "Your comment has been posted." });
      setNewCommentText("");
      setReplyingTo(null);
      setReplyText("");
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  // Edit comment — update in cache directly (no refetch to avoid branch DB staleness)
  const editMutation = useMutation({
    mutationFn: async ({ id, commentText }: { id: string; commentText: string }) => {
      const res = await apiRequest("PATCH", `/api/crm/deals/${dealId}/comments/${id}`, { commentText });
      return res.json() as Promise<DealComment>;
    },
    onSuccess: (updatedComment: DealComment) => {
      const currentComments = queryClient.getQueryData<DealComment[]>(commentsQueryKey) ?? [];
      queryClient.setQueryData(
        commentsQueryKey,
        currentComments.map(c => c.id === updatedComment.id ? updatedComment : c)
      );
      toast({ title: "Comment updated", description: "Your comment has been updated." });
      setEditingId(null);
      setEditText("");
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  // Delete comment — optimistic cache update via setQueryData (idiomatic RQ v5 pattern)
  // Matches createMutation/editMutation approach for consistency
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/crm/deals/${dealId}/comments/${id}`);
    },
    onMutate: async (deletedId: string) => {
      // Cancel outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: commentsQueryKey });
      const previous = queryClient.getQueryData<DealComment[]>(commentsQueryKey);
      // Remove the comment and its replies from cache immediately
      queryClient.setQueryData<DealComment[]>(commentsQueryKey, (old) =>
        (old ?? []).filter(c => c.id !== deletedId && c.parentCommentId !== deletedId)
      );
      setDeleteComment(null);
      return { previous };
    },
    onSuccess: () => {
      toast({ title: "Comment deleted", description: "The comment and its replies have been removed." });
    },
    onError: (error: Error, _deletedId: string, context) => {
      // Rollback: restore previous cache state
      if (context?.previous) {
        queryClient.setQueryData(commentsQueryKey, context.previous);
      }
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
    onSettled: () => {
      // Refetch to sync with server after mutation settles (success or error)
      queryClient.invalidateQueries({ queryKey: commentsQueryKey });
    },
  });

  // ==============================
  // HANDLERS
  // ==============================

  const handlePostComment = () => {
    if (!newCommentText.trim()) return;
    createMutation.mutate({ commentText: newCommentText.trim(), isInternal });
  };

  const handleReply = (commentId: string) => {
    setReplyingTo(commentId);
    setReplyText("");
    // Cancel any edit
    setEditingId(null);
    setEditText("");
  };

  const handleSubmitReply = () => {
    if (!replyText.trim() || !replyingTo) return;
    createMutation.mutate({
      commentText: replyText.trim(),
      isInternal,
      parentCommentId: replyingTo,
    });
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyText("");
  };

  const handleEdit = (commentId: string) => {
    const comment = comments.find((c) => c.id === commentId);
    if (!comment) return;
    setEditingId(commentId);
    setEditText(comment.commentText);
    // Cancel any reply
    setReplyingTo(null);
    setReplyText("");
  };

  const handleSaveEdit = () => {
    if (!editingId || !editText.trim()) return;
    editMutation.mutate({ id: editingId, commentText: editText.trim() });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const handleDelete = (comment: DealComment) => {
    setDeleteComment(comment);
  };

  const handleConfirmDelete = () => {
    if (!deleteComment) return;
    deleteMutation.mutate(deleteComment.id);
  };

  // ==============================
  // RENDER
  // ==============================

  return (
    <Card data-testid="panel-deal-comments">
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Comments {commentCount > 0 && `(${commentCount})`}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Loading */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : topLevelComments.length === 0 ? (
          /* Empty state */
          <div className="text-center py-8">
            <MessageSquare className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm font-medium text-muted-foreground">No comments yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Start a conversation about this deal
            </p>
          </div>
        ) : (
          /* Comments list */
          <div className="space-y-1 divide-y">
            {topLevelComments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                allComments={comments}
                dealId={dealId}
                onReply={handleReply}
                onEdit={handleEdit}
                onDelete={handleDelete}
                replyingTo={replyingTo}
                editingId={editingId}
                editText={editText}
                setEditText={setEditText}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={handleCancelEdit}
                replyText={replyText}
                setReplyText={setReplyText}
                onSubmitReply={handleSubmitReply}
                onCancelReply={handleCancelReply}
                isReplyPending={createMutation.isPending}
                isEditPending={editMutation.isPending}
              />
            ))}
          </div>
        )}

        {/* Add comment input */}
        <div className="mt-4 pt-3 border-t space-y-2">
          <Textarea
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            placeholder="Add a comment..."
            className="min-h-[80px] text-sm"
            data-testid="comment-input"
          />
          <div className="flex items-center justify-between">
            {/* Internal / Shared toggle */}
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => setIsInternal(!isInternal)}
              data-testid="toggle-visibility"
            >
              {isInternal ? (
                <>
                  <Lock className="h-3 w-3 mr-1" />
                  Internal
                </>
              ) : (
                <>
                  <Globe className="h-3 w-3 mr-1" />
                  Shared
                </>
              )}
            </Button>

            {/* Post button */}
            <Button
              size="sm"
              onClick={handlePostComment}
              disabled={!newCommentText.trim() || createMutation.isPending}
              data-testid="button-post-comment"
            >
              {createMutation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <Send className="h-3 w-3 mr-1" />
              )}
              Post Comment
            </Button>
          </div>
        </div>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteComment} onOpenChange={() => setDeleteComment(null)}>
        <AlertDialogContent data-testid="dialog-delete-comment">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete comment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will also delete any replies. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-comment"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
